// src/index.ts
import { z } from "zod";
var defineVersion = (def) => def;
var VersionedEntity = class {
  /**
   * @package
   */
  constructor(versionMap, latestVersion, getVersion) {
    this.versionMap = versionMap;
    this.latestVersion = latestVersion;
    this.getVersion = getVersion;
  }
  /**
   * Returns whether the given data is a valid entity of any version of the entity.
   * @param data The data to check
   * @returns Whether the given data is a valid entity of any version of the entity.
   */
  is(data) {
    let ver = this.getVersion(data);
    if (ver === null)
      return false;
    const verDef = this.versionMap[ver];
    if (!verDef)
      return false;
    return verDef.schema.safeParse(data).success;
  }
  /**
   * Returns whether the given data is a valid entity of the latest version of the entity.
   * @param data The data to check
   * @returns Whether the given data is a valid entity of the latest version of the entity.
   */
  isLatest(data) {
    return this.versionMap[this.latestVersion].schema.safeParse(data).success;
  }
  /**
   * Type guard that checks if data is valid for any version up to and including the specified version.
   * 
   * @param data The data to check
   * @param upToVersion The maximum version to check against (inclusive)
   * @returns true if the data is valid for any version from 1 to upToVersion,
   *          false if the data's version is higher than upToVersion or invalid
   * 
   * @example
   * ```ts
   * const entity = createVersionedEntity({ ... })
   * 
   * if (entity.isUpToVersion(data, 2)) {
   *   // data is guaranteed to be v1 or v2 (but not v3 or higher)
   * }
   * 
   * // Returns false for v3 data when checking up to v2
   * entity.isUpToVersion(v3Data, 2) // false
   * ```
   * 
   * Note: This is particularly useful for recursive entity definitions where you want to ensure
   * nested entities are at a specific version or lower.
   */
  isUpToVersion(data, upToVersion) {
    let ver = this.getVersion(data);
    if (ver === null)
      return false;
    if (ver > upToVersion)
      return false;
    const verDef = this.versionMap[ver];
    if (!verDef)
      return false;
    return verDef.schema.safeParse(data).success;
  }
  /**
   * Similar to Zod's `safeParse` method, but also migrates the data to the latest version.
   * @param data The data to parse
   * @returns The result from parsing data, if successful, older versions are migrated to the latest version
   */
  safeParse(data) {
    const ver = this.getVersion(data);
    if (ver === null) {
      return { type: "err", error: { type: "VER_CHECK_FAIL" } };
    }
    const verDef = this.versionMap[ver];
    if (!verDef) {
      return { type: "err", error: { type: "INVALID_VER" } };
    }
    const pass = verDef.schema.safeParse(data);
    if (!pass.success) {
      return {
        type: "err",
        error: {
          type: "GIVEN_VER_VALIDATION_FAIL",
          version: ver,
          versionDef: verDef,
          error: pass.error
        }
      };
    }
    let finalData = pass.data;
    for (let up = ver + 1; up <= this.latestVersion; up++) {
      const upDef = this.versionMap[up];
      if (!upDef) {
        return {
          type: "err",
          error: { type: "BUG_NO_INTERMEDIATE_FOUND", missingVer: up }
        };
      }
      if (upDef.initial) {
        return {
          type: "err",
          error: { type: "BUG_INTERMEDIATE_MARKED_INITIAL", ver: up }
        };
      }
      finalData = upDef.up(finalData);
    }
    return { type: "ok", value: finalData };
  }
  /**
   * Parses data and migrates it up to a specific version (not beyond).
   * 
   * @param data The data to parse and potentially migrate
   * @param version The target version to migrate to (will not migrate beyond this)
   * @returns A ParseResult containing either the migrated data or an error.
   *          Returns { type: "err", error: { type: "INVALID_VER" } } if data version is higher than requested.
   * 
   * @example
   * ```ts
   * // If you have v1 data and versions up to v4 exist:
   * const result = entity.safeParseUpToVersion(v1Data, 2)
   * // result will contain v2 data (not v3 or v4)
   * 
   * // Trying to parse v3 data up to v2 returns an error
   * const result = entity.safeParseUpToVersion(v3Data, 2)
   * // result: { type: "err", error: { type: "INVALID_VER" } }
   * ```
   * 
   * Note: This is particularly useful for recursive entity definitions to prevent migration
   * functions from receiving future versions they weren't designed to handle.
   */
  safeParseUpToVersion(data, version) {
    const ver = this.getVersion(data);
    if (ver === null) {
      return { type: "err", error: { type: "VER_CHECK_FAIL" } };
    }
    if (ver > version) {
      return { type: "err", error: { type: "INVALID_VER" } };
    }
    const verDef = this.versionMap[ver];
    if (!verDef) {
      return { type: "err", error: { type: "INVALID_VER" } };
    }
    const pass = verDef.schema.safeParse(data);
    if (!pass.success) {
      return {
        type: "err",
        error: {
          type: "GIVEN_VER_VALIDATION_FAIL",
          version: ver,
          versionDef: verDef,
          error: pass.error
        }
      };
    }
    let finalData = pass.data;
    for (let up = ver + 1; up <= version; up++) {
      const upDef = this.versionMap[up];
      if (!upDef) {
        return {
          type: "err",
          error: { type: "BUG_NO_INTERMEDIATE_FOUND", missingVer: up }
        };
      }
      if (upDef.initial) {
        return {
          type: "err",
          error: { type: "BUG_INTERMEDIATE_MARKED_INITIAL", ver: up }
        };
      }
      finalData = upDef.up(finalData);
    }
    return { type: "ok", value: finalData };
  }
};
function createVersionedEntity(def) {
  return new VersionedEntity(def.versionMap, def.latestVersion, def.getVersion);
}
function entityReference(entity) {
  return z.custom((data) => {
    return entity.is(data);
  }).transform((data) => {
    const parseResult = entity.safeParse(data);
    if (parseResult.type !== "ok") {
      throw new Error(
        "Invalid entity definition. `entity.is` returned success, safeParse failed."
      );
    }
    return parseResult.value;
  });
}
function entityRefUptoVersion(entity, upToVersion) {
  return z.custom((data) => {
    return entity.isUpToVersion(data, upToVersion);
  }).transform((data) => {
    const parseResult = entity.safeParseUpToVersion(data, upToVersion);
    if (parseResult.type !== "ok") {
      throw new Error(
        "Invalid entity definition. `entity.isUpToVersion` returned success, safeParse failed."
      );
    }
    return parseResult.value;
  });
}
export {
  VersionedEntity,
  createVersionedEntity,
  defineVersion,
  entityRefUptoVersion,
  entityReference
};
//# sourceMappingURL=index.js.map